import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  AgnesIconButton,
  AgnesModal,
  AgnesPageHeader,
  AgnesPopover,
  AgnesPopoverContent,
  AgnesPopoverTrigger,
  AgnesSelect,
  AgnesSelectContent,
  AgnesSelectItem,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesTabs,
  AgnesTabsContent,
  AgnesTabsList,
  AgnesTabsTrigger,
  AgnesTooltip,
} from '@agnes/ui';
import { ColumnsMenu } from '@/components/ColumnsMenu';
import {
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Download,
  Filter,
  HelpCircle,
  History,
  Info,
  Pencil,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { ExportButton } from '@/components/ExportButton';
import {
  matrizExposicao,
  matrizImpactos,
  matrizLegendas,
  matrizPrioridades,
  RISCO_COR,
  RISCO_LABEL,
} from '@/mocks/trabalhador';
import { CardTitle } from '@/components/ui/SectionCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { SearchInput } from '@/components/SearchInput';
import HorariosFilter from './HorariosFilter';
import {
  type HistoricoMovimentacao,
  type LinhaDetalhe,
  type LinhaHorario,
  type LinhaPorOcorrencia,
  PO_SORT_ALIASES,
  SORT_ALIASES,
  useHorariosStore,
} from '@/stores/useHorariosStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { type Barra } from '@/lib/jornada';
import { countFiltros, labelFiltro, protegerSelectDoFechamento } from '@/lib/filtros';
import { isPerfilAnalista } from '@/utils/perfil';
import UtilsMask from '@/utils/UtilsMask';
import { comQaNasCelulas, qaKey } from '@/lib/dataQa';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

// tag colorida de grau de risco (usa a paleta da matriz do legado)
function RiskTag({ risco }: { risco: string }) {
  const meta = RISCO_COR[risco];
  if (!meta) return <span className="text-agnes-text-muted">-</span>;
  return (
    <span
      className="inline-block rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: meta.bg, color: meta.fg }}
    >
      {RISCO_LABEL[risco] ?? risco}
    </span>
  );
}

const RISK_OPTS = Object.keys(RISCO_LABEL).map((k) => ({ value: k, label: RISCO_LABEL[k] }));

const SELECT_TRIGGER =
  '[&>[data-slot=select-value]]:min-w-0 [&>[data-slot=select-value]]:flex-1 [&>[data-slot=select-value]]:truncate [&>[data-slot=select-value]]:text-left';

// linha de estatística: label + % · dot · barra · count/total
function StatRow({
  label,
  count,
  total,
  cor,
  dec = 2,
  dataQa,
}: Omit<Barra, 'key'> & { total: number; dec?: number; dataQa: string }) {
  const p = total ? (count / total) * 100 : 0;
  const pctTxt = p === 100 ? '100%' : `${p.toFixed(dec)}%`;
  return (
    <div data-qa={dataQa} className="flex items-center gap-3 py-1.5 text-sm">
      <span data-qa={`${dataQa}-rotulo`} className="w-48 shrink-0 text-right text-agnes-text-muted">
        {label} {pctTxt}
      </span>
      <span className="h-3 w-3 shrink-0 rounded-full" style={{ background: cor }} />
      <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-agnes-bg-muted">
        <div
          className="h-full rounded-full"
          style={{ width: `${p}%`, background: p ? cor : undefined }}
        />
      </div>
      <span
        data-qa={`${dataQa}-quantidade`}
        className="w-28 shrink-0 text-right"
        style={{ color: count > 0 ? cor : undefined }}
      >
        {count} / {total}
      </span>
    </div>
  );
}

const soma = (barras: Barra[]) => barras.reduce((acc, b) => acc + b.count, 0);

function ChartCard({
  title,
  info,
  children,
  dataQa,
}: {
  title: string;
  info?: React.ReactNode;
  children: React.ReactNode;
  dataQa: string;
}) {
  return (
    <div data-qa={dataQa} className="rounded-xl border border-agnes-border p-4">
      <div className="mb-3 flex items-center justify-between">
        <CardTitle data-qa={`${dataQa}-titulo`}>{title}</CardTitle>
        {info && (
          <AgnesTooltip
            className="max-w-sm whitespace-normal"
            content={info}
            dataQa={`${dataQa}-info`}
          >
            <Info className="h-4 w-4 text-agnes-primary" />
          </AgnesTooltip>
        )}
      </div>
      {children}
    </div>
  );
}

const ETAPAS_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Etapas de Análise:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Analisar</b> - Itens importados mas ainda não analisados.
      </li>
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Apto para revisão</b> - item esta apto para a revisão técnica.
      </li>
      <li>
        <b>Revisado</b> - item esta revisado, mas o resultado ainda não foi disponibilizado ao
        cliente.
      </li>
      <li>
        <b>Analisado</b> - a análise foi concluída e o resultado esta visível ao cliente.
      </li>
    </ul>
  </div>
);

const STATUS_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Status das Análises:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Analisado</b> - a análise foi concluída e os resultados estão disponíveis.
      </li>
    </ul>
  </div>
);

const CLASSIFICACAO_ANALISE_INFO = (
  <div>
    <p className="font-semibold">Classificação das Análises:</p>
    <ul className="mt-1 list-disc space-y-0.5 pl-4">
      <li>
        <b>Em análise</b> - item esta sendo analisado.
      </li>
      <li>
        <b>Esclarecimento</b> - O item requer esclarecimento para que a análise possa ser concluída.
      </li>
      <li>
        <b>Risco detectado</b> - O item foi analisado e foram identificados pontos de atenção
        verifique as recomendações no menu de ações do item.
      </li>
      <li>
        <b>Inexpressivo</b> - O item foi analisado e não foram identificados pontos passíveis de
        crítica.
      </li>
    </ul>
  </div>
);

const EXPOSICAO_RISCO_INFO =
  'O gráfico de riscos considera apenas os itens analisados e que possuem algum risco relacionado. Os itens com risco são distribuídos de acordo com o grau de risco, para mais detalhes quanto os critérios de classificação de risco acesse a matriz de exposição.';

function ChartsCard({
  title,
  children,
  dataQa,
}: {
  title: React.ReactNode;
  children: React.ReactNode;
  dataQa: string;
}) {
  const [show, setShow] = React.useState(true);
  return (
    <div
      data-qa={dataQa}
      className="mt-4 rounded-xl border border-agnes-border bg-agnes-bg-default shadow-agnes-card"
    >
      <div className="flex items-center justify-between px-4 py-3">
        <span data-qa={`${dataQa}-titulo`} className="font-medium text-agnes-primary">
          {title}
        </span>
        <button
          type="button"
          data-qa={`${dataQa}-toggle`}
          className="flex items-center gap-1 text-sm font-medium text-agnes-primary"
          onClick={() => setShow((s) => !s)}
        >
          {show ? 'Ocultar gráficos' : 'Exibir gráficos'}
          {show ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>
      {show && (
        <div data-qa={`${dataQa}-conteudo`} className="border-t border-agnes-border p-4">
          {children}
        </div>
      )}
    </div>
  );
}

function SelectInline({
  placeholder,
  value,
  onValueChange,
  options,
  width = 'w-56',
  dataQa,
}: {
  placeholder: string;
  value: string;
  onValueChange: (v: string) => void;
  options: { value: string; label: string }[];
  width?: string;
  dataQa: string;
}) {
  return (
    <AgnesSelect value={value} onValueChange={onValueChange}>
      <AgnesSelectTrigger className={`${width} ${SELECT_TRIGGER}`} dataQa={dataQa}>
        <AgnesSelectValue placeholder={placeholder} />
      </AgnesSelectTrigger>
      <AgnesSelectContent dataQa={`${dataQa}-lista`}>
        {options.map((o) => (
          <AgnesSelectItem key={o.value} value={o.value} dataQa={dataQa}>
            {o.label}
          </AgnesSelectItem>
        ))}
      </AgnesSelectContent>
    </AgnesSelect>
  );
}

// matriz de exposição (heatmap Prioridade × Impacto + legenda) — referência estática do legado
function MatrizExposicao() {
  return (
    <div data-qa="horarios-matriz-exposicao" className="grid grid-cols-1 gap-6 lg:grid-cols-5">
      <div className="flex lg:col-span-3">
        <div className="flex items-center">
          <span className="rotate-180 bg-agnes-bg-muted px-1 py-2 text-xs text-agnes-text-muted [writing-mode:vertical-rl]">
            Prioridade
          </span>
        </div>
        <div className="flex-1">
          <div className="grid grid-cols-[56px_repeat(4,1fr)] overflow-hidden rounded-md text-xs">
            {matrizExposicao.map((row, i) => (
              <React.Fragment key={i}>
                <div className="flex items-center justify-center bg-agnes-bg-muted px-1 py-8 text-agnes-text-muted">
                  {matrizPrioridades[i]}
                </div>
                {row.map((cell, j) => (
                  <div
                    key={j}
                    className="flex items-center justify-center px-1 py-8 text-center"
                    style={{ background: RISCO_COR[cell].bg, color: RISCO_COR[cell].fg }}
                  >
                    {RISCO_LABEL[cell]}
                  </div>
                ))}
              </React.Fragment>
            ))}
            <div className="bg-agnes-bg-muted" />
            {matrizImpactos.map((im) => (
              <div
                key={im}
                className="flex items-center justify-center bg-agnes-bg-muted px-1 py-2 text-agnes-text-muted"
              >
                {im}
              </div>
            ))}
          </div>
          <div className="mt-1 bg-agnes-bg-muted py-1 text-center text-xs text-agnes-text-muted">
            Impacto
          </div>
        </div>
      </div>
      <ul data-qa="horarios-matriz-legenda" className="space-y-2 text-sm lg:col-span-2">
        {matrizLegendas.map((l) => (
          <li
            key={l.nivel}
            data-qa={`horarios-matriz-legenda-${qaKey(l.nivel)}`}
            className="rounded-md border-l-4 p-2"
            style={{ borderColor: RISCO_COR[l.nivel].bg, background: `${RISCO_COR[l.nivel].bg}12` }}
          >
            <p className="flex items-center gap-1 font-semibold text-agnes-text">
              {RISCO_LABEL[l.nivel]}
              <AgnesTooltip className="max-w-xs whitespace-normal" content={l.tooltip}>
                <HelpCircle className="h-3.5 w-3.5 text-agnes-text-muted" />
              </AgnesTooltip>
            </p>
            <p className="text-agnes-text-muted">{l.texto}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

// tabela de trabalhadores (Visão geral) — paginação server-side via store
function WorkersTable() {
  const {
    lista,
    total,
    page,
    size,
    carregandoLista,
    busca,
    setBusca,
    buscar,
    setPage,
    setSize,
    sortBy,
    sortOrder,
    setSort,
    exportando,
    exportar,
    filtros,
    opcoesFiltro,
    setFiltros,
    carregarFiltros,
  } = useHorariosStore();
  const navigate = useNavigate();
  const [movDialog, setMovDialog] = React.useState<LinhaHorario | null>(null);
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences(
    'trabalhador-horarios-visao-geral',
  );
  const listaEmpregadores = useEmpregadoresStore((s) => s.lista);
  const empregadores = React.useMemo(
    () =>
      listaEmpregadores.map((e) => ({
        value: e.cnpjFilial,
        label: e.company || e.fantasyName || e.cnpjFilial,
      })),
    [listaEmpregadores],
  );

  React.useEffect(() => {
    carregarFiltros();
  }, [carregarFiltros]);

  const columns = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r: LinhaHorario) => (
        <span className="text-agnes-text-muted">{lista.indexOf(r) + 1 + page * size}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empregador',
      width: 260,
      render: (r: LinhaHorario) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.empresa}</p>
          <p className="text-agnes-text-muted">{UtilsMask.cnpj(r.cnpj)}</p>
        </div>
      ),
    },
    { key: 'cpf', header: 'CPF', width: 130, render: (r: LinhaHorario) => UtilsMask.cpf(r.cpf) },
    {
      key: 'matricula',
      header: 'Matrícula',
      width: 130,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[120px] truncate" title={r.matricula}>
          {r.matricula}
        </span>
      ),
    },
    { key: 'nome', header: 'Nome / Nome social', width: 220 },
    {
      key: 'ocupacao',
      header: 'Ocupação',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.ocupacao}>
          {r.ocupacao}
        </span>
      ),
    },
    {
      key: 'cargo',
      header: 'Cargo',
      width: 160,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[150px] truncate" title={r.cargo}>
          {r.cargo}
        </span>
      ),
    },
    { key: 'codigoCategoria', header: 'Código de Categoria', width: 150 },
    {
      key: 'categoria',
      header: 'Categoria',
      width: 200,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[190px] truncate" title={r.categoria}>
          {r.categoria}
        </span>
      ),
    },
    { key: 'regimeTrabalhista', header: 'Regime Trabalhista', width: 160 },
    {
      key: 'regimePrevidenciario',
      header: 'Regime Previdenciário',
      width: 200,
      render: (r: LinhaHorario) =>
        r.regimePrevidenciario || <span className="text-agnes-text-muted">-</span>,
    },
    { key: 'etapa', header: 'Etapa de análise', width: 150 },
    { key: 'status', header: 'Status da Análise', width: 150 },
    { key: 'classificacao', header: 'Classificação', width: 150 },
    {
      key: 'risco',
      header: 'Grau de risco',
      width: 140,
      render: (r: LinhaHorario) => <RiskTag risco={r.risco as string} />,
    },
    { key: 'regimeJornada', header: 'Regime de jornada', width: 200 },
    { key: 'tipoJornada', header: 'Tipo de jornada', width: 200 },
    { key: 'cargaHorariaSemanal', header: 'Carga horária semanal', width: 180 },
    { key: 'tempoParcial', header: 'Tempo parcial', width: 180 },
    { key: 'horarioNoturno', header: 'Horário noturno', width: 150 },
    {
      key: 'descricaoJornada',
      header: 'Descrição da jornada',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoJornada as string}>
          {r.descricaoJornada as string}
        </span>
      ),
    },
    { key: 'dsr', header: 'DSR', width: 120 },
    { key: 'identificacaoEvento', header: 'Identificação do evento (S-2200/S-2206)', width: 220 },
    { key: 'envioEvento', header: 'Envio do evento', width: 150 },
    { key: 'versaoEsocial', header: 'Versão eSocial', width: 130 },
    { key: 'declaracao', header: 'Declaração', width: 150 },
    { key: 'reciboRetificacao', header: 'Recibo de retificação', width: 180 },
    {
      key: 'ocorrenciaRec',
      header: 'Ocorrência (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.ocorrenciaRec as string}>
          {r.ocorrenciaRec as string}
        </span>
      ),
    },
    {
      key: 'diagnosticoRec',
      header: 'Diagnóstico (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.diagnosticoRec as string}>
          {r.diagnosticoRec as string}
        </span>
      ),
    },
    { key: 'recRegimeJornada', header: 'Regime de jornada (Recomendação Agnes)', width: 220 },
    { key: 'recTipoJornada', header: 'Tipo de jornada (Recomendação Agnes)', width: 220 },
    {
      key: 'recCargaHorariaSemanal',
      header: 'Carga horária semanal (Recomendação Agnes)',
      width: 220,
    },
    { key: 'recTempoParcial', header: 'Tempo parcial (Recomendação Agnes)', width: 200 },
    { key: 'recHorarioNoturno', header: 'Horário noturno (Recomendação Agnes)', width: 200 },
    {
      key: 'recDescricaoJornada',
      header: 'Descrição da jornada (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.recDescricaoJornada as string}>
          {r.recDescricaoJornada as string}
        </span>
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 100,
      align: 'center' as const,
      toggleable: false,
      render: (r: LinhaHorario) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            aria-label="Editar"
            size="sm"
            dataQa={`horarios-visao-acao-editar-${r._key}`}
            disabled={r.analiseId == null}
            onClick={() =>
              r.analiseId != null && navigate(`/esocial/trabalhador/horarios/${r.analiseId}`)
            }
          >
            <Pencil className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Histórico"
            size="sm"
            dataQa={`horarios-visao-acao-historico-${r._key}`}
            disabled={!r.historicoDisponivel}
            title={r.historicoDisponivel ? 'Histórico' : 'Sem histórico'}
            onClick={() => setMovDialog(r)}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  // CON-5188: marca sortable as colunas que o backend sabe ordenar (têm alias em SORT_ALIASES).
  const cols = comQaNasCelulas(
    'horarios-visao-tabela',
    columns.map((c) => (SORT_ALIASES[c.key] ? { ...c, sortable: true } : c)),
    (r) => r._key,
  );

  return (
    <>
      <AgnesCard className="mt-4">
        <AgnesCardContent data-qa="horarios-visao-tabela-card" className="flex items-stretch">
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-[320px] flex-1">
              <SearchInput
                dataQa="horarios-visao-busca"
                value={busca}
                onValueChange={setBusca}
                onSearch={buscar}
                placeholder="Pesquisar por empregador, CPF, matrícula, nome, cargo ou ocupação..."
              />
            </div>
            <HorariosFilter
              opcoes={opcoesFiltro}
              empregadores={empregadores}
              onApply={setFiltros}
              count={countFiltros(filtros, { dateRanges: [['envioInicio', 'envioFim']] })}
            />
            <ColumnsMenu
              columns={columns}
              hiddenColumnKeys={hiddenColumnKeys}
              onToggleColumn={toggleColumn}
              data-qa="horarios-colunas"
            />
            <ExportButton
              dataQa="horarios-visao-exportar"
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              disabled={exportando}
              onClick={exportar}
            >
              Exportar
            </ExportButton>
          </div>
          {!carregandoLista && lista.length === 0 ? (
            <EmptyState
              dataQa="horarios-visao-vazio"
              className="mt-4"
              title="Nenhum trabalhador para exibir"
            />
          ) : (
            <TableWrapper className="mt-4">
              <AgnesDataTable<LinhaHorario>
                dataQa="horarios-visao-tabela"
                columns={cols}
                hiddenColumnKeys={hiddenColumnKeys}
                data={lista}
                rowKey={(r) => r._key}
                loading={carregandoLista}
                sort={{ sortBy, sortOrder, onSortChange: setSort }}
                pagination={{
                  page,
                  pageSize: size,
                  total,
                  serverSide: true,
                  onPageChange: setPage,
                  onPageSizeChange: setSize,
                }}
              />
            </TableWrapper>
          )}
        </AgnesCardContent>
      </AgnesCard>
      <MovimentacoesDialog linha={movDialog} onClose={() => setMovDialog(null)} />
    </>
  );
}

// popover Filtro da aba Risco > Todos (Grau de risco + Ocorrência)
function RiscoTodosFilter() {
  const { ocorrencias, setRtFiltros } = useHorariosStore();
  const rtRisk = useHorariosStore((s) => s.rtRisk);
  const rtOccurrence = useHorariosStore((s) => s.rtOccurrence);
  const nFiltros = countFiltros({ risk: rtRisk, occurrence: rtOccurrence });
  const [open, setOpen] = React.useState(false);
  const [risco, setRisco] = React.useState('');
  const [ocorrencia, setOcorrencia] = React.useState('');
  const ocOpts = ocorrencias.map((o) => ({ value: String(o.id), label: o.occurrence }));

  const aplicar = () => {
    setRtFiltros({
      risk: risco ? [risco] : [],
      occurrence: ocorrencia ? [Number(ocorrencia)] : [],
    });
    setOpen(false);
  };
  const limpar = () => {
    setRisco('');
    setOcorrencia('');
    setRtFiltros({ risk: [], occurrence: [] });
  };

  return (
    <AgnesPopover open={open} onOpenChange={setOpen}>
      <AgnesPopoverTrigger asChild>
        <AgnesButton
          variant="outlined"
          startIcon={<Filter className="h-4 w-4" data-qa="horarios-risco-filtro-icone-filtro" />}
          data-qa="horarios-risco-filtro-trigger"
          aria-label={nFiltros > 0 ? `Filtro, ${nFiltros} ativos` : 'Filtro'}
        >
          {labelFiltro('Filtro', nFiltros)}
        </AgnesButton>
      </AgnesPopoverTrigger>
      <AgnesPopoverContent
        align="end"
        className="w-80 p-0"
        dataQa="horarios-risco-filtro-popover"
        onInteractOutside={protegerSelectDoFechamento}
      >
        <div className="flex items-center justify-between border-b border-agnes-border px-4 py-3">
          <span className="text-base font-semibold text-agnes-text">Filtro</span>
          <button
            type="button"
            data-qa="horarios-risco-filtro-fechar"
            aria-label="Fechar"
            onClick={() => setOpen(false)}
            className="text-agnes-text-muted hover:text-agnes-text"
          >
            <X className="h-5 w-5" data-qa="horarios-risco-filtro-fechar-icone" />
          </button>
        </div>
        <div className="flex flex-col gap-3 px-4 py-4">
          <SelectInline
            dataQa="horarios-risco-filtro-campo-risco"
            placeholder="Grau de risco"
            value={risco}
            onValueChange={setRisco}
            options={RISK_OPTS}
            width="w-full"
          />
          <SelectInline
            dataQa="horarios-risco-filtro-campo-ocorrencia"
            placeholder="Ocorrência"
            value={ocorrencia}
            onValueChange={setOcorrencia}
            options={ocOpts}
            width="w-full"
          />
        </div>
        <div className="flex flex-col gap-2 border-t border-agnes-border px-4 py-3">
          <AgnesButton
            variant="primary"
            onClick={aplicar}
            className="w-full"
            data-qa="horarios-risco-filtro-aplicar"
          >
            Aplicar filtro
          </AgnesButton>
          <AgnesButton
            variant="outlined"
            onClick={limpar}
            data-qa="horarios-risco-filtro-limpar"
            startIcon={<Trash2 className="h-4 w-4" data-qa="horarios-risco-filtro-limpar-icone" />}
            className="w-full"
          >
            Limpar filtro
          </AgnesButton>
        </div>
      </AgnesPopoverContent>
    </AgnesPopover>
  );
}

// tabela de trabalhadores com risco detectado (aba Risco > Todos) — server-side
function RiscoTodosTable() {
  const {
    rtLista,
    rtTotal,
    rtPage,
    rtSize,
    rtCarregando,
    rtBusca,
    setRtBusca,
    buscarRt,
    setRtPage,
    setRtSize,
    rtSortBy,
    rtSortOrder,
    setRtSort,
    rtExportando,
    exportarRiscoTodos,
  } = useHorariosStore();
  const [movDialog, setMovDialog] = React.useState<LinhaHorario | null>(null);
  const navigate = useNavigate();
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences(
    'trabalhador-horarios-risco-todos',
  );

  const columns = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r: LinhaHorario) => (
        <span className="text-agnes-text-muted">{rtLista.indexOf(r) + 1 + rtPage * rtSize}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empregador',
      width: 260,
      render: (r: LinhaHorario) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.empresa}</p>
          <p className="text-agnes-text-muted">{UtilsMask.cnpj(r.cnpj)}</p>
        </div>
      ),
    },
    { key: 'cpf', header: 'CPF', width: 130, render: (r: LinhaHorario) => UtilsMask.cpf(r.cpf) },
    {
      key: 'matricula',
      header: 'Matrícula',
      width: 130,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[120px] truncate" title={r.matricula}>
          {r.matricula}
        </span>
      ),
    },
    { key: 'nome', header: 'Nome / Nome social', width: 220 },
    {
      key: 'ocupacao',
      header: 'Ocupação',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.ocupacao}>
          {r.ocupacao}
        </span>
      ),
    },
    {
      key: 'cargo',
      header: 'Cargo',
      width: 160,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[150px] truncate" title={r.cargo}>
          {r.cargo}
        </span>
      ),
    },
    {
      key: 'risco',
      header: 'Grau de risco',
      width: 140,
      render: (r: LinhaHorario) => <RiskTag risco={r.risco} />,
    },
    { key: 'classificacao', header: 'Classificação', width: 160 },
    { key: 'regimeJornada', header: 'Regime de jornada', width: 200 },
    { key: 'tipoJornada', header: 'Tipo de jornada', width: 200 },
    { key: 'cargaHorariaSemanal', header: 'Carga horária semanal', width: 180 },
    { key: 'tempoParcial', header: 'Tempo parcial', width: 180 },
    { key: 'horarioNoturno', header: 'Horário noturno', width: 150 },
    {
      key: 'descricaoJornada',
      header: 'Descrição da jornada',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.descricaoJornada as string}>
          {r.descricaoJornada as string}
        </span>
      ),
    },
    { key: 'dsr', header: 'DSR', width: 120 },
    { key: 'identificacaoEvento', header: 'Identificação do evento (S-2200/S-2206)', width: 220 },
    { key: 'envioEvento', header: 'Envio do evento', width: 150 },
    { key: 'versaoEsocial', header: 'Versão eSocial', width: 130 },
    { key: 'declaracao', header: 'Declaração', width: 150 },
    { key: 'reciboRetificacao', header: 'Recibo de retificação', width: 180 },
    {
      key: 'ocorrenciaRec',
      header: 'Ocorrência (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.ocorrenciaRec as string}>
          {r.ocorrenciaRec as string}
        </span>
      ),
    },
    {
      key: 'diagnosticoRec',
      header: 'Diagnóstico (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.diagnosticoRec as string}>
          {r.diagnosticoRec as string}
        </span>
      ),
    },
    { key: 'recRegimeJornada', header: 'Regime de jornada (Recomendação Agnes)', width: 220 },
    { key: 'recTipoJornada', header: 'Tipo de jornada (Recomendação Agnes)', width: 220 },
    {
      key: 'recCargaHorariaSemanal',
      header: 'Carga horária semanal (Recomendação Agnes)',
      width: 220,
    },
    { key: 'recTempoParcial', header: 'Tempo parcial (Recomendação Agnes)', width: 200 },
    { key: 'recHorarioNoturno', header: 'Horário noturno (Recomendação Agnes)', width: 200 },
    {
      key: 'recDescricaoJornada',
      header: 'Descrição da jornada (Recomendação Agnes)',
      width: 240,
      render: (r: LinhaHorario) => (
        <span className="block max-w-[220px] truncate" title={r.recDescricaoJornada as string}>
          {r.recDescricaoJornada as string}
        </span>
      ),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 100,
      align: 'center' as const,
      toggleable: false,
      render: (r: LinhaHorario) => (
        <div className="flex items-center justify-center gap-1">
          <AgnesIconButton
            aria-label="Editar"
            size="sm"
            dataQa={`horarios-risco-todos-acao-editar-${r._key}`}
            disabled={r.analiseId == null}
            onClick={() =>
              r.analiseId != null && navigate(`/esocial/trabalhador/horarios/${r.analiseId}`)
            }
          >
            <Pencil className="h-4 w-4" />
          </AgnesIconButton>
          <AgnesIconButton
            aria-label="Histórico"
            dataQa={`horarios-risco-todos-acao-historico-${r._key}`}
            size="sm"
            title={r.analiseId == null ? 'Sem análise' : 'Histórico'}
            disabled={r.analiseId == null}
            onClick={() => setMovDialog(r)}
          >
            <History className="h-4 w-4" />
          </AgnesIconButton>
        </div>
      ),
    },
  ];

  // CON-5188: sortable nas colunas com alias no backend (mesmo endpoint da Visão geral).
  const cols = comQaNasCelulas(
    'horarios-risco-todos-tabela',
    columns.map((c) => (SORT_ALIASES[c.key] ? { ...c, sortable: true } : c)),
    (r) => r._key,
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            dataQa="horarios-risco-todos-busca"
            value={rtBusca}
            onValueChange={setRtBusca}
            onSearch={buscarRt}
            placeholder="Pesquisar por CPF, matrícula, nome, cargo ou ocupação..."
          />
        </div>
        <RiscoTodosFilter />
        <ColumnsMenu
          columns={columns}
          hiddenColumnKeys={hiddenColumnKeys}
          onToggleColumn={toggleColumn}
          data-qa="horarios-risco-todos-colunas"
        />
        <ExportButton
          dataQa="horarios-risco-todos-exportar"
          variant="outlined"
          startIcon={<Download className="h-4 w-4" />}
          disabled={rtExportando}
          onClick={exportarRiscoTodos}
        >
          Exportar
        </ExportButton>
      </div>
      {!rtCarregando && rtLista.length === 0 ? (
        <EmptyState
          dataQa="horarios-risco-todos-vazio"
          className="mt-3"
          title="Nenhum trabalhador com risco para exibir"
        />
      ) : (
        <div className="mt-3 overflow-x-auto">
          <AgnesDataTable<LinhaHorario>
            dataQa="horarios-risco-todos-tabela"
            columns={cols}
            hiddenColumnKeys={hiddenColumnKeys}
            data={rtLista}
            rowKey={(r) => r._key}
            loading={rtCarregando}
            sort={{ sortBy: rtSortBy, sortOrder: rtSortOrder, onSortChange: setRtSort }}
            pagination={{
              page: rtPage,
              pageSize: rtSize,
              total: rtTotal,
              serverSide: true,
              onPageChange: setRtPage,
              onPageSizeChange: setRtSize,
            }}
          />
        </div>
      )}
      <MovimentacoesDialog linha={movDialog} onClose={() => setMovDialog(null)} />
    </>
  );
}

// dialog com o histórico de movimentações (mudanças da análise) de um trabalhador
function MovimentacoesDialog({
  linha,
  onClose,
}: {
  linha: LinhaHorario | null;
  onClose: () => void;
}) {
  const { fetchMovimentacoes, exportarMovimentacoes, movExportando } = useHorariosStore();
  const [linhas, setLinhas] = React.useState<HistoricoMovimentacao[]>([]);
  const [carregando, setCarregando] = React.useState(false);
  const analiseId = linha?.analiseId ?? null;

  React.useEffect(() => {
    if (analiseId == null) {
      setLinhas([]);
      return;
    }
    setCarregando(true);
    fetchMovimentacoes(analiseId)
      .then((rows) => setLinhas(rows))
      .finally(() => setCarregando(false));
  }, [analiseId, fetchMovimentacoes]);

  const columns = [
    {
      key: 'changedFieldDescription',
      header: 'Campo alterado',
      width: 220,
      render: (r: HistoricoMovimentacao) => r.changedFieldDescription ?? r.changedField ?? '-',
    },
    {
      key: 'fromValue',
      header: 'De',
      width: 200,
      render: (r: HistoricoMovimentacao) =>
        r.fromValue || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'toValue',
      header: 'Para',
      width: 200,
      render: (r: HistoricoMovimentacao) =>
        r.toValue || <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'updatedBy',
      header: 'Alterado por',
      width: 180,
      render: (r: HistoricoMovimentacao) =>
        r.updatedByName ?? r.updatedBy ?? <span className="text-agnes-text-muted">-</span>,
    },
    {
      key: 'updatedAt',
      header: 'Data',
      width: 180,
      render: (r: HistoricoMovimentacao) =>
        r.updatedAt || <span className="text-agnes-text-muted">-</span>,
    },
  ];

  return (
    <AgnesModal
      open={linha != null}
      title="Histórico"
      maxWidth="lg"
      dataQa="horarios-historico-modal"
      onClose={onClose}
      footer={
        <div className="flex justify-end px-2">
          <AgnesButton variant="secondary" onClick={onClose} data-qa="horarios-historico-fechar">
            Fechar
          </AgnesButton>
        </div>
      }
    >
      <div className="px-6 py-4">
        {linha && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p data-qa="horarios-historico-trabalhador" className="text-sm text-agnes-text-muted">
              <span className="font-medium text-agnes-text">{linha.nome}</span> ·{' '}
              {UtilsMask.cpf(linha.cpf)} · {linha.empresa}
            </p>
            <ExportButton
              dataQa="horarios-historico-exportar"
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              disabled={movExportando || analiseId == null}
              onClick={() => analiseId != null && exportarMovimentacoes(analiseId)}
            >
              Exportar
            </ExportButton>
          </div>
        )}
        {!carregando && linhas.length === 0 ? (
          <EmptyState dataQa="horarios-historico-vazio" title="Nenhum histórico registrado" />
        ) : (
          <div className="overflow-x-auto">
            <AgnesDataTable<HistoricoMovimentacao>
              dataQa="horarios-historico-tabela"
              columns={comQaNasCelulas('horarios-historico-tabela', columns, (r) =>
                String(r.id ?? `${r.changedField}-${r.updatedAt}`),
              )}
              data={linhas}
              rowKey={(r) => String(r.id ?? `${r.changedField}-${r.updatedAt}`)}
              loading={carregando}
              pagination={{
                page: 0,
                pageSize: 10,
                total: linhas.length,
                onPageChange: () => {},
                onPageSizeChange: () => {},
              }}
            />
          </div>
        )}
      </div>
    </AgnesModal>
  );
}

// dialog com os trabalhadores de uma ocorrência
function TrabalhadoresDialog({
  ocorrencia,
  onClose,
}: {
  ocorrencia: LinhaPorOcorrencia | null;
  onClose: () => void;
}) {
  const fetchDetalhe = useHorariosStore((s) => s.fetchDetalhe);
  const exportarDetalhe = useHorariosStore((s) => s.exportarPorOcorrenciaDetalhe);
  const exportando = useHorariosStore((s) => s.poDetalheExportando);
  const [linhas, setLinhas] = React.useState<LinhaDetalhe[]>([]);
  const [carregando, setCarregando] = React.useState(false);

  React.useEffect(() => {
    if (!ocorrencia || ocorrencia.occurrenceId == null) return;
    setCarregando(true);
    fetchDetalhe(ocorrencia.cnpj, ocorrencia.occurrenceId)
      .then((rows) => setLinhas(rows))
      .finally(() => setCarregando(false));
  }, [ocorrencia, fetchDetalhe]);

  const columns = [
    { key: 'nome', header: 'Nome', width: 260 },
    { key: 'matricula', header: 'Matrícula', width: 140 },
    { key: 'cpf', header: 'CPF', width: 160, render: (r: LinhaDetalhe) => UtilsMask.cpf(r.cpf) },
  ];

  return (
    <AgnesModal
      open={ocorrencia != null}
      title="Trabalhadores da ocorrência"
      maxWidth="lg"
      dataQa="horarios-trabalhadores-modal"
      onClose={onClose}
      footer={
        <div className="flex justify-end px-2">
          <AgnesButton
            variant="secondary"
            onClick={onClose}
            data-qa="horarios-trabalhadores-fechar"
          >
            Fechar
          </AgnesButton>
        </div>
      }
    >
      <div className="px-6 py-4">
        {ocorrencia && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <p
              data-qa="horarios-trabalhadores-ocorrencia"
              className="text-sm text-agnes-text-muted"
            >
              <span className="font-medium text-agnes-text">{ocorrencia.occurrence}</span> ·{' '}
              {ocorrencia.empresa}
            </p>
            <ExportButton
              dataQa="horarios-trabalhadores-exportar"
              variant="outlined"
              startIcon={<Download className="h-4 w-4" />}
              disabled={exportando || ocorrencia.occurrenceId == null}
              onClick={() =>
                ocorrencia.occurrenceId != null &&
                exportarDetalhe(ocorrencia.cnpj, ocorrencia.occurrenceId)
              }
            >
              Exportar
            </ExportButton>
          </div>
        )}
        {!carregando && linhas.length === 0 ? (
          <EmptyState
            dataQa="horarios-trabalhadores-vazio"
            title="Nenhum trabalhador para exibir"
          />
        ) : (
          <div className="overflow-x-auto">
            <AgnesDataTable<LinhaDetalhe>
              dataQa="horarios-trabalhadores-tabela"
              columns={comQaNasCelulas('horarios-trabalhadores-tabela', columns, (r) => r._key)}
              data={linhas}
              rowKey={(r) => r._key}
              loading={carregando}
              pagination={{
                page: 0,
                pageSize: 10,
                total: linhas.length,
                onPageChange: () => {},
                onPageSizeChange: () => {},
              }}
            />
          </div>
        )}
      </div>
    </AgnesModal>
  );
}

// tabela agrupada por ocorrência (aba Risco > Por ocorrência) — server-side
function PorOcorrenciaTable() {
  const {
    poLista,
    poTotal,
    poPage,
    poSize,
    poCarregando,
    poBusca,
    setPoBusca,
    buscarPo,
    setPoRiskLevel,
    setPoPage,
    setPoSize,
    poSortBy,
    poSortOrder,
    setPoSort,
    poExportando,
    exportarPorOcorrencia,
  } = useHorariosStore();
  const [dialog, setDialog] = React.useState<LinhaPorOcorrencia | null>(null);
  const [risco, setRisco] = React.useState('');
  const { hiddenColumnKeys, toggleColumn } = useColumnPreferences(
    'trabalhador-horarios-por-ocorrencia',
  );

  const columns = [
    {
      key: 'idx',
      header: '',
      width: 48,
      toggleable: false,
      render: (r: LinhaPorOcorrencia) => (
        <span className="text-agnes-text-muted">{poLista.indexOf(r) + 1 + poPage * poSize}</span>
      ),
    },
    {
      key: 'empresa',
      header: 'Empregador',
      width: 260,
      render: (r: LinhaPorOcorrencia) => (
        <div className="leading-tight">
          <p className="text-agnes-text">{r.empresa}</p>
          <p className="text-agnes-text-muted">{UtilsMask.cnpj(r.cnpj)}</p>
        </div>
      ),
    },
    {
      key: 'risco',
      header: 'Grau de risco',
      width: 130,
      render: (r: LinhaPorOcorrencia) => <RiskTag risco={r.risk} />,
    },
    {
      key: 'occurrence',
      header: 'Ocorrência (Recomendação)',
      width: 300,
      render: (r: LinhaPorOcorrencia) => (
        <span className="block max-w-[280px] truncate" title={r.occurrence}>
          {r.occurrence}
        </span>
      ),
    },
    {
      key: 'diagnosis',
      header: 'Diagnóstico (Recomendação)',
      width: 300,
      render: (r: LinhaPorOcorrencia) => (
        <span className="block max-w-[280px] truncate" title={r.diagnosis}>
          {r.diagnosis}
        </span>
      ),
    },
    { key: 'occurrenceCount', header: 'Quantidade de Ocorrências', width: 200 },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center' as const,
      toggleable: false,
      render: (r: LinhaPorOcorrencia) => (
        <AgnesIconButton
          aria-label="Ver trabalhadores"
          size="sm"
          dataQa={`horarios-por-ocorrencia-acao-trabalhadores-${r._key}`}
          onClick={() => setDialog(r)}
        >
          <Users className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  // CON-5188: sortable nas colunas com alias no endpoint /com-risco/por-ocorrencia.
  const cols = comQaNasCelulas(
    'horarios-por-ocorrencia-tabela',
    columns.map((c) => (PO_SORT_ALIASES[c.key] ? { ...c, sortable: true } : c)),
    (r) => r._key,
  );

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[320px] flex-1">
          <SearchInput
            dataQa="horarios-por-ocorrencia-busca"
            value={poBusca}
            onValueChange={setPoBusca}
            onSearch={buscarPo}
            placeholder="Pesquisar por ocorrência ou diagnóstico"
          />
        </div>
        <SelectInline
          dataQa="horarios-por-ocorrencia-grau-risco"
          placeholder="Grau de risco"
          value={risco}
          onValueChange={(v) => {
            setRisco(v);
            setPoRiskLevel(v ? [v] : []);
          }}
          options={RISK_OPTS}
        />
        <ColumnsMenu
          columns={columns}
          hiddenColumnKeys={hiddenColumnKeys}
          onToggleColumn={toggleColumn}
          data-qa="horarios-por-ocorrencia-colunas"
        />
        <ExportButton
          dataQa="horarios-por-ocorrencia-exportar"
          variant="outlined"
          startIcon={<Download className="h-4 w-4" />}
          disabled={poExportando}
          onClick={exportarPorOcorrencia}
        >
          Exportar
        </ExportButton>
      </div>
      {!poCarregando && poLista.length === 0 ? (
        <EmptyState
          dataQa="horarios-por-ocorrencia-vazio"
          className="mt-3"
          title="Nenhuma ocorrência para exibir"
        />
      ) : (
        <div className="mt-3 overflow-x-auto">
          <AgnesDataTable<LinhaPorOcorrencia>
            dataQa="horarios-por-ocorrencia-tabela"
            columns={cols}
            hiddenColumnKeys={hiddenColumnKeys}
            data={poLista}
            rowKey={(r) => r._key}
            loading={poCarregando}
            sort={{ sortBy: poSortBy, sortOrder: poSortOrder, onSortChange: setPoSort }}
            pagination={{
              page: poPage,
              pageSize: poSize,
              total: poTotal,
              serverSide: true,
              onPageChange: setPoPage,
              onPageSizeChange: setPoSize,
            }}
          />
        </div>
      )}
      <TrabalhadoresDialog ocorrencia={dialog} onClose={() => setDialog(null)} />
    </>
  );
}

export default function PageTrabalhadorHorarios() {
  const [tab, setTab] = React.useState('visao');
  const [subRisco, setSubRisco] = React.useState('exposicao');
  const [tabelaRisco, setTabelaRisco] = React.useState('todos');
  const [empregadorSel, setEmpregadorSel] = React.useState('');

  const empregadores = useEmpregadoresStore((s) => s.lista);
  const opcoesEmpregador = React.useMemo(
    () =>
      empregadores.map((e) => ({
        value: e.cnpjFilial,
        label: e.company || e.fantasyName || e.cnpjFilial,
      })),
    [empregadores],
  );

  const {
    stepVM,
    statusVM,
    classificationVM,
    riskVM,
    riskTotal,
    aplicarEmpregador,
    carregarVisaoGeral,
    carregarRisco,
    carregarRiscoTodos,
    carregarPorOcorrencia,
    carregarOcorrencias,
  } = useHorariosStore();

  // Perfil analista → gráfico de Etapa; cliente → gráfico de Status (paridade legado).
  const analista = React.useMemo(() => isPerfilAnalista(), []);

  React.useEffect(() => {
    useEmpregadoresStore.getState().buscar();
  }, []);

  // carrega a tabela ativa da aba Risco (Todos / Por ocorrência)
  const carregarTabelaRisco = React.useCallback(
    (sub: string) => {
      if (sub === 'todos') carregarRiscoTodos();
      else if (sub === 'ocorrencia') carregarPorOcorrencia();
    },
    [carregarRiscoTodos, carregarPorOcorrencia],
  );

  const recarregarAba = React.useCallback(
    (t: string) => {
      if (t === 'visao') {
        carregarVisaoGeral();
      } else if (t === 'risco') {
        carregarRisco();
        carregarOcorrencias();
        carregarTabelaRisco(tabelaRisco);
      }
    },
    [carregarVisaoGeral, carregarRisco, carregarOcorrencias, carregarTabelaRisco, tabelaRisco],
  );

  // carga única da Visão geral no mount (carregarVisaoGeral é ação estável do store);
  // não depende de recarregarAba p/ não refazer fetch ao trocar a sub-aba de Risco.
  React.useEffect(() => {
    carregarVisaoGeral();
  }, [carregarVisaoGeral]);

  const onTab = (t: string) => {
    setTab(t);
    recarregarAba(t);
  };
  const onTabelaRisco = (sub: string) => {
    setTabelaRisco(sub);
    carregarTabelaRisco(sub);
  };
  const aplicarFiltro = async () => {
    await aplicarEmpregador(empregadorSel);
    recarregarAba(tab);
  };
  const limparFiltro = async () => {
    setEmpregadorSel('');
    await aplicarEmpregador('');
    recarregarAba(tab);
  };

  const stepTotal = soma(stepVM);
  const statusTotal = soma(statusVM);
  const classTotal = soma(classificationVM);

  return (
    <>
      <AgnesPageHeader
        dataQa="horarios-cabecalho"
        icon={<CalendarClock />}
        title="Horários e jornadas"
        subtitle="Monitore e analise a jornada e os horários dos trabalhadores enviados ao eSocial"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Trabalhador' },
          { label: 'Horários e jornadas' },
        ]}
      />

      <div className="mt-2 rounded-2xl border border-agnes-border bg-agnes-bg-surface">
        <AgnesTabs value={tab} onValueChange={onTab} dataQa="horarios-abas">
          <AgnesTabsList className="px-4 pt-2" dataQa="horarios-abas-lista">
            <AgnesTabsTrigger value="visao" dataQa="horarios-aba-visao">
              Visão geral
            </AgnesTabsTrigger>
            <AgnesTabsTrigger value="risco" dataQa="horarios-aba-risco">
              Risco
            </AgnesTabsTrigger>
          </AgnesTabsList>

          {/* ── Visão geral ── */}
          <AgnesTabsContent
            value="visao"
            className="px-4 pb-4"
            dataQa="horarios-aba-visao-conteudo"
          >
            <div className="flex flex-wrap items-center justify-end gap-3">
              <SelectInline
                dataQa="horarios-visao-empregador"
                placeholder="Empregador"
                value={empregadorSel}
                onValueChange={setEmpregadorSel}
                options={opcoesEmpregador}
              />
              <AgnesButton
                variant="primary"
                onClick={aplicarFiltro}
                data-qa="horarios-visao-empregador-aplicar"
              >
                Aplicar filtro
              </AgnesButton>
              <AgnesButton
                variant="outlined"
                onClick={limparFiltro}
                data-qa="horarios-visao-empregador-limpar"
                startIcon={
                  <Trash2 className="h-4 w-4" data-qa="horarios-visao-empregador-limpar-icone" />
                }
              >
                Limpar filtro
              </AgnesButton>
            </div>

            <ChartsCard
              dataQa="horarios-visao-graficos"
              title={
                <>
                  Gráficos{' '}
                  <span className="font-normal">
                    por etapa de análise e classificação de análise
                  </span>
                </>
              }
            >
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {analista ? (
                  <ChartCard
                    dataQa="horarios-grafico-etapas"
                    title="Etapas de análise"
                    info={ETAPAS_ANALISE_INFO}
                  >
                    {stepVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        valor={s.valor}
                        cor={s.cor}
                        total={stepTotal}
                        dataQa={`horarios-grafico-etapas-${qaKey(s.key)}`}
                      />
                    ))}
                  </ChartCard>
                ) : (
                  <ChartCard
                    dataQa="horarios-grafico-status"
                    title="Status da análise"
                    info={STATUS_ANALISE_INFO}
                  >
                    {statusVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        valor={s.valor}
                        cor={s.cor}
                        total={statusTotal}
                        dataQa={`horarios-grafico-status-${qaKey(s.key)}`}
                      />
                    ))}
                  </ChartCard>
                )}
                <ChartCard
                  dataQa="horarios-grafico-classificacao"
                  title="Classificação de análise"
                  info={CLASSIFICACAO_ANALISE_INFO}
                >
                  {classificationVM.map((s) => (
                    <StatRow
                      key={s.key}
                      label={s.label}
                      count={s.count}
                      valor={s.valor}
                      cor={s.cor}
                      total={classTotal}
                      dataQa={`horarios-grafico-classificacao-${qaKey(s.key)}`}
                    />
                  ))}
                </ChartCard>
              </div>
            </ChartsCard>

            <WorkersTable />
          </AgnesTabsContent>

          {/* ── Risco ── */}
          <AgnesTabsContent value="risco" className="p-4" dataQa="horarios-aba-risco-conteudo">
            <div className="flex flex-wrap items-center justify-end gap-3">
              <SelectInline
                dataQa="horarios-risco-empregador"
                placeholder="Empregador"
                value={empregadorSel}
                onValueChange={setEmpregadorSel}
                options={opcoesEmpregador}
              />
              <AgnesButton
                variant="secondary"
                onClick={aplicarFiltro}
                data-qa="horarios-risco-empregador-aplicar"
              >
                Aplicar filtro
              </AgnesButton>
              <AgnesButton
                variant="secondary"
                onClick={limparFiltro}
                data-qa="horarios-risco-empregador-limpar"
                startIcon={
                  <Trash2 className="h-4 w-4" data-qa="horarios-risco-empregador-limpar-icone" />
                }
              >
                Limpar filtro
              </AgnesButton>
            </div>

            <ChartsCard
              dataQa="horarios-risco-graficos"
              title={
                <>
                  Gráficos{' '}
                  <span className="font-normal">por exposição ao risco e matriz de exposição</span>
                </>
              }
            >
              <AgnesTabs
                value={subRisco}
                onValueChange={setSubRisco}
                dataQa="horarios-risco-subabas"
              >
                <AgnesTabsList dataQa="horarios-risco-subabas-lista">
                  <AgnesTabsTrigger value="exposicao" dataQa="horarios-risco-subaba-exposicao">
                    Exposição ao risco
                  </AgnesTabsTrigger>
                  <AgnesTabsTrigger value="matriz" dataQa="horarios-risco-subaba-matriz">
                    Matriz de exposição
                  </AgnesTabsTrigger>
                </AgnesTabsList>
                <AgnesTabsContent
                  value="exposicao"
                  className="pt-4"
                  dataQa="horarios-risco-subaba-exposicao-conteudo"
                >
                  <div className="flex justify-end">
                    <AgnesTooltip
                      className="max-w-xs whitespace-normal"
                      content={EXPOSICAO_RISCO_INFO}
                      dataQa="horarios-risco-exposicao-info"
                    >
                      <Info className="h-4 w-4 text-agnes-primary" />
                    </AgnesTooltip>
                  </div>
                  {riskTotal === 0 ? (
                    <p
                      data-qa="horarios-risco-exposicao-vazio"
                      className="py-8 text-center text-agnes-text-muted"
                    >
                      Sem trabalhadores com análise concluída.
                    </p>
                  ) : (
                    riskVM.map((s) => (
                      <StatRow
                        key={s.key}
                        label={s.label}
                        count={s.count}
                        valor={s.valor}
                        cor={s.cor}
                        total={riskTotal}
                        dec={0}
                        dataQa={`horarios-risco-exposicao-${qaKey(s.key)}`}
                      />
                    ))
                  )}
                </AgnesTabsContent>
                <AgnesTabsContent
                  value="matriz"
                  className="pt-4"
                  dataQa="horarios-risco-subaba-matriz-conteudo"
                >
                  <MatrizExposicao />
                </AgnesTabsContent>
              </AgnesTabs>
            </ChartsCard>

            <div className="mt-4 rounded-xl border border-agnes-border">
              <AgnesTabs
                value={tabelaRisco}
                onValueChange={onTabelaRisco}
                dataQa="horarios-risco-tabelas"
              >
                <AgnesTabsList className="px-4 pt-2" dataQa="horarios-risco-tabelas-lista">
                  <AgnesTabsTrigger value="todos" dataQa="horarios-risco-tabela-todos">
                    Todos
                  </AgnesTabsTrigger>
                  <AgnesTabsTrigger value="ocorrencia" dataQa="horarios-risco-tabela-ocorrencia">
                    Por ocorrência
                  </AgnesTabsTrigger>
                </AgnesTabsList>
                <AgnesTabsContent
                  value="todos"
                  className="p-4"
                  dataQa="horarios-risco-tabela-todos-conteudo"
                >
                  <RiscoTodosTable />
                </AgnesTabsContent>
                <AgnesTabsContent
                  value="ocorrencia"
                  className="p-4"
                  dataQa="horarios-risco-tabela-ocorrencia-conteudo"
                >
                  <PorOcorrenciaTable />
                </AgnesTabsContent>
              </AgnesTabs>
            </div>
          </AgnesTabsContent>
        </AgnesTabs>
      </div>
    </>
  );
}
